import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Users,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Download,
  RefreshCw,
  Star,
  MessageSquare,
  FileText,
  Zap,
  Info,
  ArrowUp,
  ArrowDown,
  Loader2
} from 'lucide-react';
import { getAnalyticsMetrics, exportAnalyticsToCSV, refreshAnalytics } from '@/services/aiAnalyticsService';
import { createComponentLogger } from '@/utils/logger';
import type { AnalyticsMetrics } from '@/types/ai-admin';
import MetricCards from './MetricCards';
import CostAnalysisChart from './CostAnalysisChart';
import UsageTrendsChart from './UsageTrendsChart';
import TopQuestionsTable from './TopQuestionsTable';
import DocumentEffectivenessChart from './DocumentEffectivenessChart';
import ModelComparisonChart from './ModelComparisonChart';
import ErrorAnalysisPanel from './ErrorAnalysisPanel';

const log = createComponentLogger('AnalyticsDashboard', 'ai-analytics');

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsMetrics | null>(null);
  const [dateRange, setDateRange] = useState<'7' | '30' | '90'>('30');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load analytics data on mount and when date range changes
  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const metrics = await getAnalyticsMetrics(dateRange);
      setAnalytics(metrics);
      log.info('Analytics data loaded', { dateRange });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load analytics';
      setError(errorMessage);
      log.error('Failed to load analytics data', { dateRange }, error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refreshAnalytics();
      await loadAnalyticsData();
      log.info('Analytics refreshed successfully');
    } catch (error) {
      log.error('Failed to refresh analytics', {}, error as Error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    if (!analytics) {
return;
}

    const csvContent = exportAnalyticsToCSV(analytics);

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analytics-${dateRange}-days-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error Loading Analytics</AlertTitle>
        <AlertDescription>
          {error}
          <Button
            variant="outline"
            size="sm"
            onClick={loadAnalyticsData}
            className="ml-4"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No analytics data available for the selected period.
      </div>
    );
  }

  // Calculate cost optimization
  const currentMonthlyCost = analytics.costBreakdown.projection.monthly;
  const optimizedCost = currentMonthlyCost * 0.1; // 90% reduction with prompt caching
  const potentialSavings = currentMonthlyCost - optimizedCost;

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Select value={dateRange} onValueChange={(value: any) => setDateRange(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Cost Optimization Alert */}
      {potentialSavings > 10 && (
        <Alert>
          <Zap className="h-4 w-4" />
          <AlertTitle>Cost Optimization Available</AlertTitle>
          <AlertDescription>
            Enable prompt caching to reduce costs by 90%. Current monthly cost: ${currentMonthlyCost.toFixed(2)}.
            Potential cost with caching: ${optimizedCost.toFixed(2)}.
            <strong> Save ${potentialSavings.toFixed(2)}/month!</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* Metric Cards */}
      <MetricCards analytics={analytics} />

      {/* Main Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <UsageTrendsChart data={analytics.trends.daily} />
        <CostAnalysisChart analytics={analytics} />
      </div>

      {/* Secondary Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <DocumentEffectivenessChart documents={analytics.documentUsage} />
        <ModelComparisonChart models={analytics.modelComparison} />
      </div>

      {/* Tables and Details */}
      <div className="grid gap-6 md:grid-cols-2">
        <TopQuestionsTable questions={analytics.topQuestions} />
        <ErrorAnalysisPanel
          errorAnalysis={analytics.errorAnalysis}
          totalQueries={analytics.summary.totalQueries}
        />
      </div>

      {/* Cost Projections */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Projections & Optimization</CardTitle>
          <CardDescription>
            Based on current usage patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Daily Average</p>
              <p className="text-2xl font-bold">
                ${analytics.costBreakdown.projection.daily.toFixed(2)}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Monthly Projection</p>
              <p className="text-2xl font-bold">
                ${analytics.costBreakdown.projection.monthly.toFixed(2)}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Annual Projection</p>
              <p className="text-2xl font-bold">
                ${analytics.costBreakdown.projection.annual.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-2">💡 Optimization Recommendations</h4>
            <ul className="space-y-1 text-sm">
              <li>• Enable prompt caching for 90% cost reduction</li>
              <li>• Use Haiku for simple queries (currently {((analytics.modelComparison.find(m => m.model.includes('haiku'))?.queries || 0) / Math.max(analytics.summary.totalQueries, 1) * 100).toFixed(0)}%)</li>
              <li>• Implement smart routing based on query complexity</li>
              <li>• Set up daily cost alerts at {'$'}5 threshold</li>
              <li>• Review and optimize underutilized documents</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}